/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../redux";
import moment from "moment";
import classNames from "classnames";

import CampaignDetails from "./campaign-details";
import CampaignReport from "./campaign-report";
import CampaignSchedule from "./campaign-schedule";

import PageTitle from "../../components/page-title";
import Topbar from "../../components/topbar";
import Button from "../../components/button";
import Loader from "../../components/loader";
import NoData from "../../components/no-data";
import FormInput from "../../components/form-input";
import NewCampaignModal from "../../components/modal/new-campaign-modal";
import ConfirmationModal from "../../components/modal/confirmation-modal";
import CampaignAIAgentModal from "../../components/modal/campaign-ai-agent-modal";
import CampaignRecepientModal from "../../components/modal/campaign-recepients-modal";
import CampaignScheduleModal from "../../components/modal/campaign-schedule-modal";
import SelectCampaignSenderModal from "../../components/modal/select-campaign-sender-modal";
import KlaviyoAuthModal from "../../components/modal/klaviyo-auth-modal";
import KlaviyoTemplateModal from "../../components/modal/klaviyo-template-modal";
import UpdateBrandAssetsModal from "../../components/modal/update-brand-assets-modal";

import {
  getCampaignsAction,
  getCampaignsByIdAction,
  postAddCampaignAction,
  deleteCampaignAction,
  rewriteCampaignMsgAction,
  sendCampaignEmailAction,
  postUpdateCampaignAction,
  getCampaignEmailAnalyticsAction,
  scheduleCampaignAction,
  pauseCampaignAction,
  continueCampaignAction,
  endCampaignAction,
  addCampaignForPersonalizationAction,
  getPersonalizedCampaignsByIdAction,
  rewriteCampaignPreviewBodyAction,
} from "../../redux/dashboard/campaigns-slice";
import { getBrandDataAction } from "../../redux/dashboard/brand-slice";
import { getStoreBrandAssetsAction } from "../../redux/auth/user-slice";
import { setAlert } from "../../redux/components/components-slice";

import Icon from "../../assets/svg";

import { APP_LIST_LAYOUT_LIST } from "../../utils/constants";
import { scheduleKlaviyoCampaignSend } from "../../redux/dashboard/klaviyo-slice";
import KonvasCampaignTemplatesModal from "../../components/modal/konvas-templates-modal";
import {
  convertEscapedStringToHTML,
  isValidJson,
  tourGuideCheck,
} from "../../utils/functions";
import queryString from "query-string";
import CampaignRecipientLimitModal from "../../components/modal/campaign-recipient-limit-modal";
import CheckCampaignMessageModal from "../../components/modal/check-campaign-message-modal";
import GuidedTour from "../../components/guided-tour";

import { useGuidedTour } from "../../hooks/useGuidedTour";
import { CallBackProps } from "react-joyride";

const listOrderOptions = [
  { label: "Most recent", value: "desc" },
  { label: "Oldest", value: "asc" },
];

const statusOptions = [
  {
    label: "Draft",
    value: "draft",
    content:
      "Here, you will find your campaign drafts. When you create a campaign, I save it to drafts. 😎",
  },
  {
    label: "Scheduled",
    value: "scheduled",
    content:
      "When you schedule a campaign in drafts, it moves to the scheduled tab.",
  },
  {
    label: "Active",
    value: "active",
    content: "When a campaign starts running, it moves to the active tab. ",
  },
  {
    label: "Completed",
    value: "completed",
    content:
      "Once a campaign is done running, it moves to the completed tab and the user will be able to  see the campaign reports.",
  },
];

const campaignStatusSteps = statusOptions.map((item) => ({
  target: `#status-${item.value}`,
  content: item.content,
  disableBeacon: true,
}));

const campaignDashboardTourSteps = [
  ...campaignStatusSteps,
  {
    target: "#new-campaign-btn",
    content: "Use this button to create a new campaign.",
  },
];

//Tour guide steps for campaign details
const campaignDetailsTourSteps = [
  {
    target: "#campaign-title-box",
    content:
      "Here, you can see the title, rationale and the number of recipients for this campaign.",
    disableBeacon: true,
  },
  {
    target: "#brand-tone-select",
    content:
      'Campaign message generated based on default tags selected. For a different message tone for this campaign, remove or replace any of the tags below and click "Rewrite Message". Select a min of 1 and a max of 2 tags.',
  },
  {
    target: ".writing_approach",
    content:
      "This box contains various writing approach. You can change the approach your campaign was generated with.",
  },
  {
    target: ".additional_context",
    content:
      "This is a free textbox to provide additional instructions and you can use this context to rewrite your campaign message.",
  },
  {
    target: "#regenerate-campaign-btn",
    content:
      "After you change a tone or writing approach or use the free textbox to give me additional context, click here to rewrite the campaign message.",
  },
  {
    target: "#preview-campaign-btn",
    content:
      "Click here to see 3 examples of personalized messages tailored to each customer.",
  },
  {
    target: "#campaign-date-change",
    content: "You can change the date of your campaign by clicking this button",
  },
  {
    target: "#preview-template_body",
    content:
      "Observe how the generated campaign message adapts to the layout and style of your selected template",
  },
  {
    target: "#campaign-edit-icon",
    content: "Use this icon to edit your campaign message ",
  },
  {
    target: "#schedule-campaign",
    content:
      "This button enables you to schedule your campaign. Ensure you update your brand assets in settings and change the header image before scheduling. 👍",
  },
  {
    target: "#delete-campaign",
    content:
      "Click on this button to delete a campaign message. Once a message is deleted, you wont be able to get it back.",
  },
  {
    target: "#bottom_text",
    content:
      "Read more to know how we generate personalized campaign messages. 😎",
  },
  {
    target: "#start-campaign-btn",
    content:
      "Use this button to start your campaign.  Ensure you update your brand assets in settings and change the header image before starting this campaign. 👍",
  },
];

const CampaignsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();

  //Tracking initial render for tour guide tooltips
  const isFirstRender = useRef(true);

  //Tracking initial render for campaign status change
  const isInitialRender = useRef(true);

  const { brandData, isFetchingBrandData } = useSelector(
    (state: any) => state.brandSlice
  );

  const { campaignId, campaignStatus } = queryString.parse(location.search);
  const isExplicitlySearching = location.search.includes("campaignId");

  const updateUrlWithCampaign = (id: string | null) => {
    if (id) {
      history.push({
        pathname: "/campaigns",
        search: `?campaignStatus=${status}&campaignId=${id}`,
      });
    } else {
      history.push({
        pathname: `/campaigns`,
        search: `?campaignStatus=${status}`,
      });
    }
  };

  const { listLayout } = useSelector((state: any) => state.componentsSlice);
  const {
    isFetchingCampaigns,
    campaigns,
    isFetchingCampaignData,
    isDeletingCampaign,
    isEndingCampaign,
    isContinuingCampaign,
    isPausingCampaign,
    isSchedulingCampaign,
    isUpdatingCampaign,
    isStartingCampaign,
  } = useSelector((state: any) => state.campaignsSlice);

  const [campaignsData, setCampaignsData] = useState(campaigns || []);
  const [listOrder, setListOrder] = useState(listOrderOptions[0]);
  const [status, setStatus] = useState(statusOptions[0].value);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedWritingApproach, setSelectedWritingApproach] = useState("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isRewritingMsg, setIsRewritingMsg] = useState(false);
  const [isRewritingPreviewMessages, setIsRewritingPreviewMessages] =
    useState(false);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgContent, setMsgContent] = useState("");
  const [isEditingMsgContent, setIsEditingMsgContent] = useState(false);
  const [analytics, setAnalytics] = useState({});

  //state for NewCampaignModal component
  const [newCampaignModal, setNewCampaignModal] = useState(false);
  const [campaignModalStep, setCampaignModalStep] = useState<number>(1);
  const [campaignModalType, setCampaignModalType] = useState<any>(null);

  const [updateBrandAssetsModal, setUpdateBrandAssetsModal] = useState(false);
  const [isCampaignAiAgentModalOpen, setIsCampaignAiAgentModalOpen] =
    useState(false);
  const [isCampaignRecepientModalOpen, setIsCampaignRecepientModalOpen] =
    useState(false);
  const [isCampaignScheduleModalOpen, setIsCampaignScheduleModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCampaignReportExpanded, setIsCampaignReportExpanded] =
    useState(false);

  const [isSelectCampaignSenderModalOpen, setIsSelectCampaignSenderModalOpen] =
    useState(false);
  const [isKlaviyoAuthModalOpen, setIsKlaviyoAuthModalOpen] = useState(false);
  const [
    isKonvasCampaignTemplatesModalOpen,
    setIsKonvasCampaignTemplatesModalOpen,
  ] = useState(false);
  const [isKlaviyoTemplateModalOpen, setIsKlaviyoTemplateModalOpen] =
    useState(false);
  const [
    isCampaignRecipientLimitModalOpen,
    setIsCampaignRecipientLimitModalOpen,
  ] = useState(false);
  const [checkCampaignMessageModal, setCheckCampaignMessageModal] =
    useState(false);

  const [campaignSender, setCampaignSender] = useState("");

  const [isCampaignDateChange, setIsCampaignDateChange] = useState(false);
  const [sendTo5000Anyway, setSendTo5000Anyway] = useState(false);
  const [saveEditedTemplate, setSaveEditedTemplate] = useState(false);
  const [runCampaignDetailsTour, setRunCampaignDetailsTour] = useState(false);
  const [campaignDetailsTourStepIndex, setCampaignDetailsTourStepIndex] =
    useState(0);

  const {
    runTour,
    setRunTour,
    tourStepIndex,
    setTourStepIndex,
    isSecondTour,
    setIsSecondTour,
  } = useGuidedTour();

  //campaign type options in the NewCampaignModal component
  const campaignModalTypeOptions = [
    { label: "SEGMENT BASED CAMPAIGN", value: 1 },
    { label: "CUSTOM CAMPAIGN", value: 2 },
  ];

  //Tour guide steps for the campaign creation flow
  const createCampaignTourSteps = [
    {
      target: "#new-campaign-modal",
      content:
        "You can create your campaign from scratch with custom campaign or create for existing segments.",
      disableBeacon: true,
    },
    {
      target:
        campaignModalType?.value === 1
          ? "#segment-campaign-select"
          : "#custom-campaign-input",
      content:
        campaignModalType?.value === 1
          ? "Choose either the 'AI-Generated Segment' tab or 'Custom Segment' tab. Select a segment from the list of segments in the dropdown then click the 'Generate campaign details' button to generate campaign."
          : "Use the free textbox below to provide a prompt for your custom campaign ",
    },
    // or use “Ask AI” below to generate one for you.
    {
      target: "#select-campaign-sender",
      content: "Select a sending platform for your campaign ",
    },
    {
      target: "#konvas-template-modal",
      content: "Choose a template to launch your campaign.",
      offset: 25,
    },
  ];

  const editor = useRef(null);

  const getSunEditorInstance = (sunEditor: any) => {
    editor.current = sunEditor;
  };

  const setCampaignInitialSubjectAndContent = () => {
    setMsgSubject(
      selectedCampaign?.campaign_subject?.replace(
        "[Customer Name]",
        "{$customerName}"
      )
    );
    // setMsgContent(msgContentRevamp(selectedCampaign?.campaign_body));

    const campaignBody = selectedCampaign?.campaign_body.replace("&quot;", '"');

    console.log(
      "selectedCampaignBody:",
      convertEscapedStringToHTML(
        isValidJson(campaignBody) ? JSON.parse(campaignBody) : campaignBody
      )
    );
    setMsgContent(
      convertEscapedStringToHTML(
        isValidJson(campaignBody) ? JSON.parse(campaignBody) : campaignBody
      )
    );
  };

  const sumUpAnalyticsKey = (arr: any, key: string) => {
    return arr?.reduce((a: any, b: any) => a + (b[key] || 0), 0);
  };
  const getCampaignAnalytics = () => {
    dispatch(getCampaignEmailAnalyticsAction(selectedCampaign.id)).then(
      (res: any) => {
        const data: any = res?.data;

        const reached = sumUpAnalyticsKey(data, "delivered");
        const delivered = sumUpAnalyticsKey(data, "delivered");
        const bounced =
          sumUpAnalyticsKey(data, "soft_bounced") +
          sumUpAnalyticsKey(data, "hard_bounced");
        const opened = sumUpAnalyticsKey(data, "opened");
        const unsubscribed = sumUpAnalyticsKey(data, "unsubscribed");
        const clicked = sumUpAnalyticsKey(data, "clicked");
        const sent = sumUpAnalyticsKey(data, "sent");
        const pending = sumUpAnalyticsKey(data, "queued") - sent;
        const clicked_unique = sumUpAnalyticsKey(data, "clicked_unique");
        const opened_unique = sumUpAnalyticsKey(data, "opened_unique");

        setAnalytics({
          reached,
          delivered,
          bounced,
          opened,
          unsubscribed,
          pending,
          clicked,
          sent,
          clicked_unique,
          opened_unique,
        });
      }
    );
  };

  useEffect(() => {
    if (saveEditedTemplate) {
      updateCampaign();
    }
  }, [saveEditedTemplate]);

  useEffect(() => {
    setIsEditingMsgContent(false);
    if (selectedCampaign?.campaign_body) setCampaignInitialSubjectAndContent();

    if (selectedCampaign?.status === "active") {
      getCampaignAnalytics();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign]);

  useEffect(() => {
    if (selectedCampaign?.ai_tones) {
      try {
        const parsedTones = JSON.parse(selectedCampaign.ai_tones);
        setSelectedTags(parsedTones.map((tag: string) => ({ tag })));
      } catch (error) {
        console.error("Error parsing AI tones:", error);
        setSelectedTags([]);
      }
    }
  }, [selectedCampaign]);

  useEffect(() => {
    setCampaignsData(campaigns?.filter((x: any) => x.status === status));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, status]);

  const topBarFilters: any = [
    {
      type: "select",
      value: listOrder,
      options: listOrderOptions,
      onChange: (value: any) => setListOrder(value),
    },
  ];

  useEffect(() => {
    console.log("runTour:", runTour);
  }, [runTour]);

  useEffect(() => {
    if (isFirstRender.current) {
      // Skip this effect on first render
      isFirstRender.current = false;
      return;
    }
    if (
      !isFetchingCampaigns &&
      !isFetchingBrandData &&
      !isFetchingCampaignData
    ) {
      // console.log('first tour run...');
      tourGuideCheck("campaignTourCompleted", setRunTour);
    }
  }, [isFetchingCampaigns, isFetchingBrandData, isFetchingCampaignData]);

  useEffect(() => {
    if (newCampaignModal && isSecondTour) {
      tourGuideCheck("createCampaignTourCompleted", setRunTour);
    }
  }, [newCampaignModal]);

  useEffect(() => {
    if (selectedCampaign && campaigns?.length > 0) {
      tourGuideCheck("campaignDetailsTourCompleted", setRunCampaignDetailsTour);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    if (campaignModalStep === 2 && isSecondTour) {
      setTourStepIndex(1);
    }
    // if (campaignModalStep === 1 && isSecondTour) {
    //   setTourStepIndex(0);
    // }
  }, [campaignModalStep]);

  useEffect(() => {
    if (isSecondTour && isSelectCampaignSenderModalOpen) {
      setTourStepIndex(2);
      if (newCampaignModal) setNewCampaignModal(false);
    }
  }, [isSelectCampaignSenderModalOpen]);

  useEffect(() => {
    if (isSecondTour && isKonvasCampaignTemplatesModalOpen) {
      setTourStepIndex(3);
    } else if (!isKonvasCampaignTemplatesModalOpen && isSecondTour) {
      setRunTour(false);
    }
  }, [isKonvasCampaignTemplatesModalOpen]);

  useEffect(() => {
    const loadInitialCampaign = async () => {
      if (campaignId && typeof campaignId === "string") {
        await getCampaignById(campaignId);
      }
      if (campaignStatus && typeof campaignStatus === "string")
        setStatus(campaignStatus);
    };

    loadInitialCampaign();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, campaignStatus]);

  useEffect(() => {
    if (isInitialRender.current) {
      // Skip this effect on first render
      isInitialRender.current = false;
      return;
    }
    updateUrlWithCampaign(null);
  }, [status]);

  const getCampaigns = async (value?: string) => {
    // if (value === "custom") {
    //   setSegmentData([]);
    // } else {
    //   dispatch(getCampaignsAction());
    // }

    await dispatch(getCampaignsAction());
  };

  useEffect(() => {
    getCampaigns();
    dispatch(getBrandDataAction());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCampaignById = async (id: string) => {
    try {
      const res = await dispatch(getCampaignsByIdAction(id));
      if (res?.success === true) {
        // updateUrlWithCampaign(id);
        setSelectedCampaign(res?.data);
        setCampaignSender(
          res?.data?.email_channel === "konvas"
            ? "Konvas AI"
            : !res?.data?.email_channel
            ? "Konvas AI"
            : res?.data?.email_channel
        );
        localStorage.setItem("__created_campaign", JSON.stringify(res?.data));
        localStorage.removeItem("konvas_campaign_id");
        setSelectedTags(
          JSON.parse(res?.data?.ai_tones)?.map((tag: string) => ({ tag }))
        );
        setSelectedWritingApproach(res?.data?.writing_approach);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setSelectedCampaign(null);
      updateUrlWithCampaign(null);
    }
  };

  const postAddCampaign = async (data: {
    segment_id: string;
    payload: any;
  }) => {
    try {
      const res = await dispatch(postAddCampaignAction(data));
      if (res?.success === true) {
        // await getCampaignById(res?.campaignId);
        updateUrlWithCampaign(res?.campaignId);
        setNewCampaignModal(false);
        setIsKonvasCampaignTemplatesModalOpen(false);
        // if (res?.campaign?.status === "draft")
        //   setIsSelectCampaignSenderModalOpen(true);
        await getCampaigns();
        setStatus(res?.campaign?.status);
        dispatch(
          addCampaignForPersonalizationAction({
            id: Number(res.campaignId),
            ...data.payload,
          })
        );

        return res;
      }
    } catch (error) {
      throw error;
    }
  };

  const updateCampaign = async () => {
    setIsRewritingMsg(true);

    const payload = {
      id: selectedCampaign?.id,
      campaign_subject: msgSubject,
      campaign_body: msgContent,
    };

    await dispatch(postUpdateCampaignAction(payload))
      .then((res) => {
        if (res?.success === true) {
          if (saveEditedTemplate) setSaveEditedTemplate(false);
          getCampaignById(selectedCampaign?.id);
        }
      })
      .catch((error) => {
        console.log("error updating campaign:", error);
        if (saveEditedTemplate) setSaveEditedTemplate(false);
      });

    setIsRewritingMsg(false);
  };

  const endCampaign = () => {
    const payload = {
      id: selectedCampaign?.id,
    };

    dispatch(endCampaignAction(payload)).then((res: any) => {
      if (res?.success === true) {
        getCampaigns();
        setStatus(statusOptions[3].value);
        setSelectedCampaign(null);
        getCampaignById(selectedCampaign?.id);
      }
    });
  };

  const pauseCampaign = () => {
    const payload = {
      id: selectedCampaign?.id,
    };

    dispatch(pauseCampaignAction(payload)).then((res: any) => {
      if (res?.success === true) {
        getCampaigns();
        setSelectedCampaign(null);
        getCampaignById(selectedCampaign?.id);
      }
    });
  };

  const continueCampaign = () => {
    const payload = {
      id: selectedCampaign?.id,
    };

    dispatch(continueCampaignAction(payload)).then((res: any) => {
      if (res?.success === true) {
        getCampaigns();
        setSelectedCampaign(null);
        getCampaignById(selectedCampaign?.id);
      }
    });
  };

  const scheduleCampaign = (start_date: string, end_date: string) => {
    const state = localStorage.getItem("asset_state");

    const payload: any = {
      id: selectedCampaign?.id,
      state: Number(state),
      slug: selectedCampaign?.slug,
      campaign_title: selectedCampaign?.campaign_title,
      start_date,
      end_date,
    };

    if (isCampaignDateChange) {
      delete payload.state;
      delete payload.slug;
      dispatch(postUpdateCampaignAction(payload)).then((res: any) => {
        if (res?.success === true) {
          setIsCampaignDateChange(false);
          setIsCampaignScheduleModalOpen(false);
          // getCampaigns();
          getCampaignById(selectedCampaign?.id);
        }
      });
    } else {
      delete payload.campaign_title;

      console.log("schedule_payload", payload);
      dispatch(scheduleCampaignAction(payload)).then((res: any) => {
        if (res?.success === true) {
          localStorage.setItem("asset_state", "0");
          setIsCampaignScheduleModalOpen(false);
          getCampaigns();
          setStatus(statusOptions[1].value);
          setSelectedCampaign(null);
          getCampaignById(selectedCampaign?.id);
        }
      });
    }
  };

  useEffect(() => {
    if (sendTo5000Anyway) {
      startCampaign();
    }
  }, [sendTo5000Anyway]);

  const convertRecipientData = (
    entries: string[]
  ): { email: string; name: string }[] => {
    return entries.map((entry) => {
      const [email, name] = entry.split(";");
      return { email, name };
    });
  };

  const getHeroImageSrc = (htmlString: string): string | null => {
    // Parse the HTML string into a document
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Find the img element with alt="Hero Image"
    const heroImage = doc.querySelector('img[alt="Hero Image"]');

    // Return the src attribute value or null if not found
    return heroImage ? heroImage.getAttribute("src") : null;
  };

  const startCampaign = () => {
    let campaign_recipients = selectedCampaign?.campaign_recipients?.data;

    const campaignRecipientsLength =
      selectedCampaign?.campaign_recipients?.pagination?.total_recipients;

    if (campaignRecipientsLength > 5000 && !sendTo5000Anyway)
      return setIsCampaignRecipientLimitModalOpen(true);

    const heroImgSrc = getHeroImageSrc(msgContent);

    const isHeroImageChanged = heroImgSrc?.includes(
      "https://cdn.grapesjs.com/workspaces/"
    );

    if (!isHeroImageChanged) return setCheckCampaignMessageModal(true);
    campaign_recipients = convertRecipientData(campaign_recipients);

    const state = localStorage.getItem("asset_state");

    const payload = {
      state: Number(state),
      business_slug: selectedCampaign?.slug,
      campaign_id: selectedCampaign?.id,
      campaign_subject: msgSubject,
      campaign_recipients,
    };

    if (campaignSender === "Klaviyo") {
      const klaviyoCampaignId = localStorage.getItem("klaviyo_campaign_id");
      const payload = {
        data: {
          type: "campaign-send-job",
          id: klaviyoCampaignId || selectedCampaign?.klaviyo_campaign_id,
        },
      };
      dispatch(scheduleKlaviyoCampaignSend(payload))
        .then((res: any) => {
          if (res?.success === true) {
            localStorage.removeItem("klaviyo_campaign_id");
            if (isCampaignRecipientLimitModalOpen)
              setIsCampaignRecipientLimitModalOpen(false);
            setSendTo5000Anyway(false);
            getCampaigns();
            setStatus(statusOptions[2].value);
            setSelectedCampaign(null);
            getCampaignById(selectedCampaign?.id);
          }
        })
        .catch((error) => {
          console.log("error starting campaign:", error);
          if (sendTo5000Anyway) setSendTo5000Anyway(false);
        });
    } else {
      dispatch(sendCampaignEmailAction(payload))
        .then((res: any) => {
          if (res?.success === true) {
            localStorage.setItem("asset_state", "0");
            if (isCampaignRecipientLimitModalOpen)
              setIsCampaignRecipientLimitModalOpen(false);
            setSendTo5000Anyway(false);
            getCampaigns();
            setStatus(statusOptions[2].value);
            setSelectedCampaign(null);
            getCampaignById(selectedCampaign?.id);
          }
        })
        .catch((error) => {
          console.log("error staring campaign:", error);
          if (sendTo5000Anyway) setSendTo5000Anyway(false);
        });
    }
  };

  const deleteCampaign = () => {
    dispatch(deleteCampaignAction(selectedCampaign.id)).then((res: any) => {
      if (res?.success === true) {
        getCampaigns();
        setIsConfirmationModalOpen(false);
        setSelectedCampaign(null);
      }
    });
  };

  const groupedData = () => {
    // First, map each campaign to include the full timestamp
    const campaignsWithDate = campaignsData?.map((campaign: any) => {
      const date = new Date(
        campaign.status === "draft" ? campaign.created_at : campaign.start_date
      );
      return {
        ...campaign,
        timestamp: date.getTime(),
        // Format date without time for grouping
        dateString: date.toLocaleDateString(),
      };
    });

    // First sort by timestamp in descending order (most recent first)
    const sortedCampaigns = campaignsWithDate.sort(
      (a: any, b: any) => b.timestamp - a.timestamp
    );

    // Group by date
    const grouped = sortedCampaigns.reduce((acc: any, campaign: any) => {
      acc[campaign.dateString] = acc[campaign.dateString] || [];
      acc[campaign.dateString].push(campaign);
      return acc;
    }, Object.create(null));

    // Convert to array and ensure each group is properly sorted by time
    return (
      Object.entries(grouped)
        .map(([dateString, campaigns]: any) => ({
          createdAt: dateString,
          // Sort campaigns within each date group by timestamp in descending order
          data: campaigns.sort((a: any, b: any) => b.timestamp - a.timestamp),
        }))
        // Finally, sort the date groups themselves
        .sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        })
    );
  };

  const campaignsDataToShow =
    listOrder?.value === listOrderOptions[0].value
      ? groupedData()
      : groupedData().reverse();

  const handleSelectRow = (rowId: any) => {
    if (selectedRows?.some((x) => rowId?.includes(x))) {
      setSelectedRows(selectedRows?.filter((x: string) => x !== rowId));
    } else {
      setSelectedRows([...selectedRows, rowId]);
    }
  };

  const isRowSelected = (rowId: any) => {
    return selectedRows?.some((x) => rowId?.includes(x));
  };

  const contentOptions = [
    { label: "Edit", action: () => setIsEditingMsgContent(true) },
    // { label: "Rewrite", action: () => rewriteMsg("") },
  ];

  const checkStoreBrandAssets = async () => {
    try {
      const res = await dispatch(getStoreBrandAssetsAction());
      const brandAssets = res?.brandAssets;
      const { brand_logo_url, term_service, privacy } = brandAssets;

      const isBrandLogo =
        brand_logo_url && !brand_logo_url.includes("default-logo.png");
      const isTermsOfService =
        term_service && term_service !== "https://website.com/term_service";
      const isPrivacyPolicy =
        privacy && privacy !== "https://website.com/privacy";

      // const brandAssetsMustHave = {
      //   pry_color,
      //   brand_logo_url,
      //   term_service,
      //   privacy,
      // };
      // console.log("brandAssetsMustHave:", brandAssetsMustHave);
      // const isAllAssetsAvailable = Object.values(brandAssetsMustHave).every(
      //   (value) => !!value
      // );
      // console.log("all assets available", isAllAssetsAvailable);
      const isAllAssetsAvailable =
        isBrandLogo && isTermsOfService && isPrivacyPolicy;
      if (!isAllAssetsAvailable) {
        setUpdateBrandAssetsModal(true);
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const reWriteCampaignPreviewMessages = async (
    brandTones: string,
    additionalContext: string,
    writingApproach: string,
    initialBrandTones: string,
    initialWritingApproach: string
  ) => {
    const res = await dispatch(
      getPersonalizedCampaignsByIdAction(selectedCampaign?.id)
    );

    const campaignPreviewMessages = res?.data?.campaigns;
    console.log("campaignPreviewMessages", campaignPreviewMessages);

    const results = await Promise.all(
      campaignPreviewMessages.map((campaignPreview: any) => {
        return dispatch(
          rewriteCampaignPreviewBodyAction(
            initialBrandTones,
            initialWritingApproach,
            campaignPreview?.id,
            campaignPreview?.body,
            brandTones,
            additionalContext,
            writingApproach
          )
        );
      })
    );

    console.log("All items processed:", results);
  };

  const rewriteMsg = async (additionalContext: string) => {
    try {
      if (!isRewritingMsg) {
        setIsRewritingMsg(true);

        const currentBrandTones = selectedTags?.map(
          (data: { tag: string }) => data.tag
        );

        const parsedInitialTones = selectedCampaign.ai_tones
          ? JSON.parse(selectedCampaign.ai_tones)
          : [];

        const initialBrandTones = JSON.stringify(parsedInitialTones);
        const initialWritingApproach = selectedCampaign.writing_approach;

        const brandTones = JSON.stringify(
          currentBrandTones.length === 0
            ? parsedInitialTones
            : currentBrandTones
        );
        // const previousBrandTones = selectedCampaign.ai_tones;
        // console.log('Previous brand tones sent to API:', previousBrandTones);
        // console.log("Current brand tones sent to API:", brandTones);

        const writingApproach = selectedWritingApproach;
        // console.log("Previous writing approach sent to API:", );
        // console.log("Final writing approach sent to API:", writingApproach);

        const res = await dispatch(
          rewriteCampaignMsgAction(
            selectedCampaign?.id,
            brandTones,
            additionalContext,
            writingApproach
          )
        );
        if (res?.success === true) {
          if (res?.data) {
            console.log("initialRewrittenMessageString", res?.data);
            const emailMessage = convertEscapedStringToHTML(res?.data);
            console.log("finalFormattedRewrittenMessage", emailMessage);
            setMsgContent(emailMessage);
            const updateCampaignPayload = {
              id: selectedCampaign?.id,
              campaign_title: selectedCampaign?.campaign_title,
              ai_tones: brandTones,
              writing_approach: writingApproach,
              campaign_body: res?.data,
            };
            await dispatch(postUpdateCampaignAction(updateCampaignPayload));
          }
        }

        setIsRewritingMsg(false);
        setIsRewritingPreviewMessages(true);
        await reWriteCampaignPreviewMessages(
          brandTones,
          additionalContext,
          writingApproach,
          initialBrandTones,
          initialWritingApproach
        );

        console.log("all 3 preview rewrites completed!! ");

        setIsRewritingPreviewMessages(false);
      }
    } catch (error) {
      setIsRewritingMsg(false);
      console.log("error", error);
      dispatch(setAlert(true, "error", "Something went wrong, try again"));
    }
  };

  const dateDisplay = (startDate: Date, endDate: Date) => {
    return `${
      moment(startDate).format("MMM DD, YYYY") +
      " - " +
      moment(endDate).format("MMM DD, YYYY")
    }`;
  };

  const campaignTourCallBack = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    console.log(
      "action:",
      action,
      "index:",
      index,
      "status:",
      status,
      "type:",
      type
    );

    if (type === "step:after" && action === "prev") {
      setTourStepIndex(index - 1);
      // if (isSecondTour) {
      //   if (index === 3) {
      //     setIsKonvasCampaignTemplatesModalOpen(false);
      //     setIsSelectCampaignSenderModalOpen(true);
      //   } else if (index === 2) {
      //     setIsSelectCampaignSenderModalOpen(false);
      //     setNewCampaignModal(true);
      //     setCampaignModalStep(2);
      //   } else if (index === 1) {
      //     setCampaignModalStep(1);
      //   }
      // }
    }

    if (type === "step:after" && action === "next") {
      setTourStepIndex(index + 1);
      // if (isSecondTour) setCampaignModalStep((prev) => prev + 1);
    }
    if (status === "finished" || status === "skipped") {
      setTourStepIndex(0);
      setRunTour(false);

      if (isSecondTour) {
        localStorage.setItem("createCampaignTourCompleted", "true");
        setIsSecondTour(false);
      } else {
        setIsSecondTour(true);
        localStorage.setItem("campaignTourCompleted", "true");
      }
    }
  };

  const campaignDetailsTourCallBack = (data: CallBackProps) => {
    const { action, index, status, type } = data;

    if (type === "step:after" && action === "prev") {
      setCampaignDetailsTourStepIndex(index - 1);
    }

    if (type === "step:after" && action === "next") {
      setCampaignDetailsTourStepIndex(index + 1);
    }
    if (status === "finished" || status === "skipped") {
      localStorage.setItem("campaignDetailsTourCompleted", "true");
      setCampaignDetailsTourStepIndex(0);
      setRunCampaignDetailsTour(false);
    }
  };

  return (
    <React.Fragment>
      <GuidedTour
        steps={
          isSecondTour ? createCampaignTourSteps : campaignDashboardTourSteps
        }
        styles={{
          options: {
            overlayColor: isSecondTour ? "none" : "rgba(0, 0, 0, 0.5)",
          },
        }}
        isSpotlightClicks={isSecondTour}
        runTour={runTour}
        setRunTour={setRunTour}
        tourStepIndex={tourStepIndex}
        disableNextBtn={isSecondTour}
        hideBackBtn={isSecondTour}
        setTourStepIndex={setTourStepIndex}
        tourCallBack={campaignTourCallBack}
      />

      <GuidedTour
        steps={campaignDetailsTourSteps}
        runTour={runCampaignDetailsTour}
        setRunTour={setRunCampaignDetailsTour}
        tourStepIndex={campaignDetailsTourStepIndex}
        setTourStepIndex={setCampaignDetailsTourStepIndex}
        tourCallBack={campaignDetailsTourCallBack}
      />

      <PageTitle title="Campaigns" />
      <Topbar
        title="Campaigns"
        onSearch={() => {}}
        filters={topBarFilters}
        actionBtn={
          <Button
            id="new-campaign-btn"
            text="New Campaign"
            leftIcon="plus"
            onClick={() => {
              setNewCampaignModal(true);
            }}
          />
        }
      />

      <div className="page_container campaign_page_container">
        {(isFetchingCampaigns || isFetchingBrandData) && <Loader />}

        {!isFetchingCampaigns && !isFetchingBrandData && (
          <div
            className={classNames("campaign_page_container_inner", {
              block_display: isCampaignReportExpanded,
              is_fetching_campaign_data: isFetchingCampaignData,
            })}
          >
            <div
              className={classNames("campaigns_list", {
                is_visible: !selectedCampaign,
                not_visible: isCampaignReportExpanded,
              })}
            >
              <div className="status_sections">
                <div className="side">
                  {statusOptions?.map((item: any, i: any) => (
                    <p
                      key={i}
                      id={`status-${item.value}`}
                      className={classNames("status", {
                        active:
                          item.value === status ||
                          (item.value === status && i === tourStepIndex),
                      })}
                      onClick={() => {
                        setStatus(item.value);
                        setSelectedCampaign(null);
                        // updateUrlWithCampaign(null);
                      }}
                    >
                      {item.label}
                    </p>
                  ))}
                </div>

                <div className="side">
                  <div className="icon" onClick={() => getCampaigns()}>
                    <Icon name="refresh" />
                  </div>
                </div>
              </div>

              {listLayout === APP_LIST_LAYOUT_LIST ? (
                <div className="layout list_layout scroll-height">
                  {campaignsDataToShow?.length === 0 ? (
                    <NoData
                      text={`No  ${
                        status === "draft" ? "drafted" : status
                      } campaigns at the moment`}
                    />
                  ) : (
                    campaignsDataToShow?.map((campaign: any, index: any) => (
                      <React.Fragment key={index}>
                        <p className="date_title">
                          {/* {dateToFromNow(campaign.createdAt)} */}
                          {/* {moment(campaign?.createdAt).fromNow()} */}
                          {campaign?.createdAt}
                        </p>

                        <div className="list">
                          {campaign?.data?.map((row: any, i: any) => (
                            <div
                              key={i}
                              className={classNames("row", {
                                active: selectedCampaign?.id === row.id,
                              })}
                            >
                              <div className="left">
                                <FormInput
                                  id="selectRow"
                                  type="checkbox"
                                  value={isRowSelected(row?.campaign_id)}
                                  onChange={() =>
                                    handleSelectRow(row?.campaign_id)
                                  }
                                  readOnly={isFetchingCampaigns}
                                />

                                <p
                                  className="title"
                                  onClick={() => updateUrlWithCampaign(row?.id)}
                                >
                                  {row.campaign_title}
                                </p>
                              </div>

                              <p className="time">
                                {moment(row.created_at).format("hh: mm A")}
                              </p>
                            </div>
                          ))}
                        </div>
                      </React.Fragment>
                    ))
                  )}
                </div>
              ) : (
                // <div className="layout grid_layout">
                //   {campaignsData?.map((row: any, i: any) => (
                //     <div
                //       key={i}
                //       className={classNames("row", {
                //         active: selectedCampaign?.id === row.id,
                //       })}
                //     >
                //       <div className="row_inner">
                //         <div className="top">
                //           <p
                //             className="title"
                //             onClick={() => updateUrlWithCampaign(row?.id)}
                //           >
                //             {row.campaign_title}
                //           </p>

                //           <p className="time">
                //             {moment(row.created_at).format("MMM DD")}
                //           </p>
                //         </div>

                //         <p className="subject">{row.campaign_subject}</p>
                //         <p className="content">{row.campaign_body}</p>
                //       </div>
                //     </div>
                //   ))}
                // </div>
                <></>
              )}
            </div>

            {selectedCampaign ? (
              selectedCampaign.status === "scheduled" ? (
                <CampaignSchedule
                  campaignData={selectedCampaign}
                  isStartingCampaign={isStartingCampaign}
                  startCampaign={startCampaign}
                >
                  <CampaignDetails
                    isFetchingCampaignData={isFetchingCampaignData}
                    selectedCampaign={selectedCampaign}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    campaignSender={campaignSender}
                    setCampaignSender={setCampaignSender}
                    selectedWritingApproach={selectedWritingApproach}
                    setSelectedWritingApproach={setSelectedWritingApproach}
                    analytics={analytics}
                    setSelectedCampaign={setSelectedCampaign}
                    setIsCampaignAiAgentModalOpen={
                      setIsCampaignAiAgentModalOpen
                    }
                    setIsCampaignRecepientModalOpen={
                      setIsCampaignRecepientModalOpen
                    }
                    setIsKlaviyoAuthModalOpen={setIsKlaviyoAuthModalOpen}
                    setIsKlaviyoTemplateModalOpen={
                      setIsKlaviyoTemplateModalOpen
                    }
                    rewriteMsg={rewriteMsg}
                    setIsCampaignScheduleModalOpen={
                      setIsCampaignScheduleModalOpen
                    }
                    setIsCampaignDateChange={setIsCampaignDateChange}
                    dateDisplay={dateDisplay}
                    contentOptions={contentOptions}
                    isRewritingMsg={isRewritingMsg}
                    isRewritingPreviewMessages={isRewritingPreviewMessages}
                    isEditingMsgContent={isEditingMsgContent}
                    msgSubject={msgSubject}
                    setMsgSubject={setMsgSubject}
                    msgContent={msgContent}
                    setMsgContent={setMsgContent}
                    getSunEditorInstance={getSunEditorInstance}
                    setIsEditingMsgContent={setIsEditingMsgContent}
                    setCampaignInitialSubjectAndContent={
                      setCampaignInitialSubjectAndContent
                    }
                    updateCampaign={updateCampaign}
                    setSaveEditedTemplate={setSaveEditedTemplate}
                    isStartingCampaign={isStartingCampaign}
                    startCampaign={startCampaign}
                    setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                    isCampaignReportExpanded={isCampaignReportExpanded}
                    setIsCampaignReportExpanded={setIsCampaignReportExpanded}
                    pauseCampaign={pauseCampaign}
                    continueCampaign={continueCampaign}
                    isPausingCampaign={isPausingCampaign}
                    isContinuingCampaign={isContinuingCampaign}
                    isEndingCampaign={isEndingCampaign}
                    endCampaign={endCampaign}
                    checkStoreBrandAssets={checkStoreBrandAssets}
                  />
                </CampaignSchedule>
              ) : (
                <CampaignDetails
                  // Same props as above
                  isFetchingCampaignData={isFetchingCampaignData}
                  selectedCampaign={selectedCampaign}
                  selectedTags={selectedTags}
                  setSelectedTags={setSelectedTags}
                  campaignSender={campaignSender}
                  setCampaignSender={setCampaignSender}
                  selectedWritingApproach={selectedWritingApproach}
                  setSelectedWritingApproach={setSelectedWritingApproach}
                  analytics={analytics}
                  setSelectedCampaign={setSelectedCampaign}
                  setIsCampaignAiAgentModalOpen={setIsCampaignAiAgentModalOpen}
                  setIsCampaignRecepientModalOpen={
                    setIsCampaignRecepientModalOpen
                  }
                  setIsCampaignDateChange={setIsCampaignDateChange}
                  setIsKlaviyoAuthModalOpen={setIsKlaviyoAuthModalOpen}
                  setIsKlaviyoTemplateModalOpen={setIsKlaviyoTemplateModalOpen}
                  rewriteMsg={rewriteMsg}
                  setIsCampaignScheduleModalOpen={
                    setIsCampaignScheduleModalOpen
                  }
                  dateDisplay={dateDisplay}
                  contentOptions={contentOptions}
                  isRewritingMsg={isRewritingMsg}
                  isRewritingPreviewMessages={isRewritingPreviewMessages}
                  isEditingMsgContent={isEditingMsgContent}
                  msgSubject={msgSubject}
                  setMsgSubject={setMsgSubject}
                  msgContent={msgContent}
                  setMsgContent={setMsgContent}
                  getSunEditorInstance={getSunEditorInstance}
                  setIsEditingMsgContent={setIsEditingMsgContent}
                  setCampaignInitialSubjectAndContent={
                    setCampaignInitialSubjectAndContent
                  }
                  updateCampaign={updateCampaign}
                  setSaveEditedTemplate={setSaveEditedTemplate}
                  isStartingCampaign={isStartingCampaign}
                  startCampaign={startCampaign}
                  setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                  isCampaignReportExpanded={isCampaignReportExpanded}
                  setIsCampaignReportExpanded={setIsCampaignReportExpanded}
                  pauseCampaign={pauseCampaign}
                  continueCampaign={continueCampaign}
                  isPausingCampaign={isPausingCampaign}
                  isContinuingCampaign={isContinuingCampaign}
                  isEndingCampaign={isEndingCampaign}
                  endCampaign={endCampaign}
                  checkStoreBrandAssets={checkStoreBrandAssets}
                />
              )
            ) : isExplicitlySearching && !isFetchingCampaignData ? (
              <div className="campaign_details is_visible">
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-8">
                    <h2 className="text-xl font-semibold text-gray-700 mb-2">
                      Campaign Not Found
                    </h2>
                    <p className="text-gray-500 mb-4">
                      The campaign you're looking for doesn't exist or has been
                      deleted.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {isCampaignReportExpanded && (
              <div className="campaign_details">
                <CampaignReport
                  reportData={{
                    ...analytics,
                    ...selectedCampaign,
                  }}
                  isCampaignReportExpanded={isCampaignReportExpanded}
                  setIsCampaignReportExpanded={setIsCampaignReportExpanded}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <NewCampaignModal
        isOpen={newCampaignModal}
        close={() => {
          if (runTour) setRunTour(false);
          setNewCampaignModal(false);
        }}
        open={() => setNewCampaignModal(true)}
        onSubmit={(payload) => postAddCampaign(payload)}
        brandData={brandData}
        campaignTypeOptions={campaignModalTypeOptions}
        step={campaignModalStep}
        setStep={setCampaignModalStep}
        campaignType={campaignModalType}
        setCampaignType={setCampaignModalType}
        setIsSelectCampaignSenderModalOpen={setIsSelectCampaignSenderModalOpen}
        setUpdateBrandAssetsModal={setUpdateBrandAssetsModal}
      />

      <UpdateBrandAssetsModal
        isOpen={updateBrandAssetsModal}
        close={() => setUpdateBrandAssetsModal(false)}
      />
      <CampaignAIAgentModal
        isOpen={isCampaignAiAgentModalOpen}
        close={() => setIsCampaignAiAgentModalOpen(false)}
        aiDetails={selectedCampaign?.ai_details}
      />

      {selectedCampaign ? (
        <CampaignRecepientModal
          isOpen={isCampaignRecepientModalOpen}
          close={() => setIsCampaignRecepientModalOpen(false)}
          campaignRecepients={selectedCampaign?.campaign_recipients?.data}
        />
      ) : (
        <></>
      )}

      <CampaignScheduleModal
        isOpen={isCampaignScheduleModalOpen}
        close={() => {
          setIsCampaignScheduleModalOpen(false);
          setIsCampaignDateChange(false);
        }}
        scheduleCampaign={scheduleCampaign}
        isSchedulingCampaign={isSchedulingCampaign}
        isUpdatingCampaign={isUpdatingCampaign}
        isCampaignDateChange={isCampaignDateChange}
      />

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        close={() => setIsConfirmationModalOpen(false)}
        confirmationText={`Are you sure you want to delete ${selectedCampaign?.campaign_title}?`}
        onSubmit={deleteCampaign}
        loading={isDeletingCampaign}
      />

      {isSelectCampaignSenderModalOpen && (
        <SelectCampaignSenderModal
          isOpen={isSelectCampaignSenderModalOpen}
          close={() => setIsSelectCampaignSenderModalOpen(false)}
          selectedCampaign={selectedCampaign}
          setIsKlaviyoAuthModalOpen={setIsKlaviyoAuthModalOpen}
          setIsKonvasCampaignTemplatesModalOpen={
            setIsKonvasCampaignTemplatesModalOpen
          }
        />
      )}

      <KlaviyoAuthModal
        isOpen={isKlaviyoAuthModalOpen}
        open={(isOpen: boolean) => setIsKlaviyoAuthModalOpen(isOpen)}
        selectedCampaign={selectedCampaign}
        setIsKlaviyoTemplateModalOpen={setIsKlaviyoTemplateModalOpen}
      />

      {isCampaignRecipientLimitModalOpen && (
        <CampaignRecipientLimitModal
          isOpen={isCampaignRecipientLimitModalOpen}
          close={() => setIsCampaignRecipientLimitModalOpen(false)}
          isStartingCampaign={isStartingCampaign}
          setSendTo5000Anyway={setSendTo5000Anyway}
          startCampaign={startCampaign}
        />
      )}

      {isKlaviyoTemplateModalOpen && (
        <KlaviyoTemplateModal
          isOpen={isKlaviyoTemplateModalOpen}
          selectedCampaign={selectedCampaign}
          close={() => setIsKlaviyoTemplateModalOpen(false)}
        />
      )}

      {checkCampaignMessageModal && (
        <CheckCampaignMessageModal
          isOpen={checkCampaignMessageModal}
          close={() => setCheckCampaignMessageModal(false)}
        />
      )}

      {isKonvasCampaignTemplatesModalOpen && (
        <KonvasCampaignTemplatesModal
          isOpen={isKonvasCampaignTemplatesModalOpen}
          open={(isOpen: boolean) =>
            setIsKonvasCampaignTemplatesModalOpen(isOpen)
          }
          onSubmit={(payload) => postAddCampaign(payload)}
          brandData={brandData}
        />
      )}
    </React.Fragment>
  );
};

export default CampaignsPage;
